import React from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Trash2, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent } from "./ui/sheet";
import { useStudio } from "../context/StudioContext";
import { usePlanDrawer } from "../context/PlanDrawerContext";

function parsePrice(text) {
  if (!text) return 0;
  const n = String(text).replace(/[^0-9.]/g, "");
  return parseFloat(n || "0");
}

export default function PlanDrawer() {
  const { open, setOpen } = usePlanDrawer();
  const { plan, removeFromPlan, clearPlan } = useStudio();
  const navigate = useNavigate();

  const total = plan.reduce((acc, item) => acc + parsePrice(item.price), 0);
  const totalStr = total.toLocaleString("en-IN");

  const go = (to) => {
    setOpen(false);
    setTimeout(() => navigate(to), 60);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-[420px] max-w-[92vw] border-l border-brand-hair bg-brand-panel p-0 text-brand-ivory sm:max-w-[420px]"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-brand-hair px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand text-white">
              <ShoppingBag className="h-4 w-4" />
            </div>
            <div>
              <div className="font-serif-display text-[22px] leading-none">
                Your <span className="italic text-brand">Plan</span>
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                {plan.length} {plan.length === 1 ? "item" : "items"}
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-brand-hair text-brand-mute hover:border-brand hover:text-brand"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5" style={{ maxHeight: "calc(100vh - 220px)" }}>
          {plan.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-md bg-brand/10 text-brand">
                <ShoppingBag className="h-6 w-6" />
              </div>
              <div className="mt-6 font-serif-display text-[22px]">Your plan is empty.</div>
              <p className="mt-2 max-w-[280px] text-[12.5px] text-brand-ivory/60">
                Pick services from the dashboard and they will land here.
              </p>
              <button
                onClick={() => go("/dashboard")}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.28em] text-white transition-colors hover:bg-[#ff5c26]"
              >
                Browse services <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <ul className="space-y-3">
              {plan.map((item) => (
                <li
                  key={item.key || item.code}
                  className="group rounded-md border border-brand-hair bg-brand-panel2 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">
                        {item.code}
                        {item.planName ? ` \u00b7 ${item.planName}` : ""}
                      </div>
                      <button
                        onClick={() => go(`/service/${item.serviceSlug}/${item.subSlug}`)}
                        className="mt-1 truncate text-left font-serif-display text-[17px] leading-tight tracking-tight text-brand-ivory hover:underline"
                      >
                        {item.title}
                      </button>
                      <div className="mt-1 truncate text-[11.5px] text-brand-ivory/55">
                        {item.service}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        removeFromPlan(item.key || item.code);
                        toast(`Removed ${item.title} from plan.`);
                      }}
                      className="flex h-7 w-7 flex-none items-center justify-center rounded-md border border-brand-hair text-brand-mute hover:border-brand hover:text-brand"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-brand-hair/60 pt-2 text-[12px]">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">
                      {item.billing}
                    </span>
                    <span className="font-mono text-[13px] text-brand-ivory">{item.price}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {plan.length > 0 && (
          <div className="border-t border-brand-hair px-6 py-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  Estimated total
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute/70">
                  GST extra
                </div>
              </div>
              <div className="font-serif-display text-[30px] leading-none text-brand">
                &#x20B9;{totalStr}
              </div>
            </div>
            <button
              onClick={() => {
                setOpen(false);
                setTimeout(() => navigate("/checkout"), 60);
              }}
              className="group mt-5 flex w-full items-center justify-center gap-3 rounded-md bg-brand py-3 font-mono text-[11.5px] font-semibold uppercase tracking-[0.28em] text-white transition-colors hover:bg-[#ff5c26]"
            >
              Proceed to checkout
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => {
                clearPlan();
                toast("Plan cleared.");
              }}
              className="mt-2 w-full font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute hover:text-brand"
            >
              Clear all items
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
